var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { useCallback, useEffect, useRef, useState } from 'react';
import { deleteDraft, draftKey, pruneExpiredDrafts, readDraft, writeDraft, } from '@/lib/form-drafts';
/*
 * VA-29255: keeps the job wizard's unsaved edits (step 1 JD and step 2 preferences)
 * on this device until the server has them.
 *
 * "Unsaved" is measured against a baseline: the wizard state as the server last
 * confirmed it (initial defaults for a new job, the loaded JD, or the last successful
 * step save). When the current fields differ, they are written as a draft (debounced,
 * and immediately when the tab is hidden or closed); when they match again, or a save
 * succeeds, the draft is deleted. On open, a draft that differs from the server copy is
 * offered for restore rather than applied silently.
 */
const DRAFT_VERSION = 1;
const FORM = 'job-wizard';
const WRITE_DELAY_MS = 800;
/** The recruiter's inputs on steps 1 and 2. No files, tokens or candidate data. */
const FIELDS = [
    'manualJD',
    'generatedJD',
    'jdPrompt',
    'jobTitle',
    'duration',
    'interviewLevel',
    'interview_type',
    'min_experience',
    'max_experience',
    'timeCapping',
    'noOfCodingQuestions',
    'passing_score',
    'voiceConfig',
    'ask_dynamic_question',
    'should_ask_followup_question',
    'interview_taken_by',
    'voice_provider_id',
    'voice_language',
    'voice_id',
];
/** Regeneration flags, restored as they were so Continue still regenerates what changed. */
const FLAGS = ['isDirty', 'criteriaDirty', 'timeCappingDirty', 'criteriaEdited'];
function pickFields(fd) {
    const out = {};
    FIELDS.forEach((k) => {
        const v = fd[k];
        if (v !== undefined && v !== null && v !== '')
            out[k] = v;
    });
    if (fd.uploadedFile && fd.activeTab === 'upload')
        out.hadFile = true;
    return out;
}
function snapshot(fd) {
    return JSON.stringify(pickFields(fd));
}
export function useWizardDraft({ formData, setFormData, enabled, locked }) {
    const record = formData.jobDescriptionId || 'new';
    const [status, setStatus] = useState('idle');
    const [offer, setOffer] = useState(null);
    // Bumped whenever the server confirms the current state; the effect below then
    // takes a new baseline from the formData of that render.
    const [savedTick, setSavedTick] = useState(0);
    const baselineRef = useRef(null);
    // Re-render when the baseline moves, so hasUnsavedChanges is never computed
    // against the previous one.
    const [, setBaselineVersion] = useState(0);
    // The key whose stored draft has been looked at (and offered or found absent).
    // Nothing is written or deleted for a key before that, or the first write would
    // overwrite the very draft we're about to offer.
    const [readyKey, setReadyKey] = useState(null);
    const readingRef = useRef(null);
    const recordRef = useRef(record);
    const pendingRef = useRef(null);
    const timerRef = useRef();
    const active = enabled && !locked;
    const key = active ? draftKey(FORM, record) : null;
    // Until the recruiter types or clicks, every change is the form filling itself in
    // (server data, defaults set by the step components' own effects), so it moves the
    // baseline instead of counting as unsaved work.
    const interactedRef = useRef(false);
    useEffect(() => {
        if (!active)
            return undefined;
        const mark = () => {
            interactedRef.current = true;
        };
        const events = ['keydown', 'pointerdown', 'input', 'paste', 'drop'];
        events.forEach((e) => document.addEventListener(e, mark, true));
        return () => events.forEach((e) => document.removeEventListener(e, mark, true));
    }, [active]);
    /** Call after the server accepted a save (or loaded fresh data): current state is the baseline. */
    const markSaved = useCallback(() => setSavedTick((t) => t + 1), []);
    useEffect(() => {
        void pruneExpiredDrafts();
    }, []);
    const flush = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        window.clearTimeout(timerRef.current);
        timerRef.current = undefined;
        const pending = pendingRef.current;
        if (!pending)
            return;
        pendingRef.current = null;
        const ok = yield writeDraft(pending.key, DRAFT_VERSION, pending.draft);
        setStatus(ok ? 'saved' : 'unavailable');
    }), []);
    const cancelPending = () => {
        pendingRef.current = null;
        window.clearTimeout(timerRef.current);
        timerRef.current = undefined;
    };
    // Baseline: on the first ready render, and again after every confirmed save. When a
    // new job gets its id (step 1 saved), the draft kept under 'new' is no longer needed.
    useEffect(() => {
        if (!active)
            return;
        if (recordRef.current !== record) {
            const oldKey = draftKey(FORM, recordRef.current);
            if (oldKey)
                void deleteDraft(oldKey);
            recordRef.current = record;
        }
        baselineRef.current = snapshot(formData);
        setBaselineVersion((v) => v + 1);
        cancelPending();
        if (key && readyKey === key) {
            void deleteDraft(key);
            setStatus('idle');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active, savedTick, record]);
    // Once per key: offer a stored draft that differs from what the server has.
    useEffect(() => {
        if (!key || readingRef.current === key)
            return;
        readingRef.current = key;
        void readDraft(key, DRAFT_VERSION).then((found) => {
            if (readingRef.current !== key)
                return;
            if (found && JSON.stringify(found.data.fields) !== baselineRef.current) {
                setOffer({ savedAt: found.savedAt, draft: found.data });
            }
            else if (found) {
                void deleteDraft(key);
            }
            setReadyKey(key);
        });
    }, [key]);
    // Write (debounced) whenever the fields drift from the baseline; delete when they return.
    useEffect(() => {
        if (!key || readyKey !== key || offer || baselineRef.current === null)
            return;
        const current = snapshot(formData);
        if (!interactedRef.current && current !== baselineRef.current) {
            baselineRef.current = current;
            setBaselineVersion((v) => v + 1);
            return;
        }
        if (current === baselineRef.current) {
            cancelPending();
            void deleteDraft(key);
            setStatus('idle');
            return;
        }
        const flags = {};
        FLAGS.forEach((f) => {
            if (formData[f])
                flags[f] = true;
        });
        pendingRef.current = { key, draft: { fields: JSON.parse(current), flags } };
        setStatus((s) => (s === 'unavailable' ? s : 'saving'));
        window.clearTimeout(timerRef.current);
        timerRef.current = window.setTimeout(() => void flush(), WRITE_DELAY_MS);
    }, [key, readyKey, offer, formData, flush]);
    // Save immediately when the tab is hidden or closed, and on unmount.
    useEffect(() => {
        const onHide = () => {
            if (document.visibilityState === 'hidden')
                void flush();
        };
        const onPageHide = () => void flush();
        document.addEventListener('visibilitychange', onHide);
        window.addEventListener('pagehide', onPageHide);
        return () => {
            document.removeEventListener('visibilitychange', onHide);
            window.removeEventListener('pagehide', onPageHide);
            void flush();
        };
    }, [flush]);
    const restore = useCallback(() => {
        if (!offer)
            return;
        const { fields, flags } = offer.draft;
        const { hadFile } = fields, rest = __rest(fields, ["hadFile"]);
        // Typed text lives on the "Write Manually" tab; a file goes back on "Upload".
        const typed = !!(rest.manualJD || rest.generatedJD || rest.jdPrompt);
        setFormData((prev) => (Object.assign(Object.assign(Object.assign(Object.assign({}, prev), rest), flags), { activeTab: typed ? 'manual' : hadFile ? 'upload' : prev.activeTab })));
        setOffer(null);
        return { needsFile: !!hadFile };
    }, [offer, setFormData]);
    /** "Discard" on the restore prompt. */
    const discard = useCallback(() => {
        if (key)
            void deleteDraft(key);
        setOffer(null);
        setStatus('idle');
    }, [key]);
    /** Drops this form's draft right now, e.g. "Discard & leave". */
    const discardCurrent = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        cancelPending();
        if (key)
            yield deleteDraft(key);
    }), [key]);
    const hasUnsavedChanges = active && baselineRef.current !== null && snapshot(formData) !== baselineRef.current;
    return {
        status,
        offer,
        restore,
        discard,
        discardCurrent,
        markSaved,
        hasUnsavedChanges,
        /** Writes any pending draft now, e.g. before leaving the page. */
        flush,
    };
}
/** For read-only previews of the wizard, which never keep drafts. */
export const NO_WIZARD_DRAFT = {
    status: 'idle',
    offer: null,
    restore: () => undefined,
    discard: () => undefined,
    discardCurrent: () => __awaiter(void 0, void 0, void 0, function* () { return undefined; }),
    markSaved: () => undefined,
    hasUnsavedChanges: false,
    flush: () => __awaiter(void 0, void 0, void 0, function* () { return undefined; }),
};
